import { useCallback, useEffect, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import { ensureLogoTemplateFonts } from './fonts/load';
import type { LogoTemplateProps } from './types';

type ReadyOptions = {
  onReady?: LogoTemplateProps['onReady'];
  /** Bumps when layout/text inputs change and readiness must be recomputed. */
  layoutKey: string;
};

/**
 * Waits for logo fonts, then exposes a ref callback. Call `signalReady(svg)` once
 * the SVG layout for the current layoutKey is final (after getBBox adjustments).
 */
export function useLogoTemplateReady({ onReady, layoutKey }: ReadyOptions) {
  const [fontsReady, setFontsReady] = useState(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const readyForKeyRef = useRef<string | null>(null);
  const onReadyRef = useRef(onReady);

  useIsomorphicLayoutEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    let cancelled = false;
    ensureLogoTemplateFonts().then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    // Clear readiness for this layout identity so signalReady can fire again.
    readyForKeyRef.current = `${layoutKey}:pending`;
  }, [layoutKey]);

  const setSvgRef = useCallback((node: SVGSVGElement | null) => {
    svgRef.current = node;
  }, []);

  const signalReady = useCallback(
    (svg: SVGSVGElement) => {
      if (readyForKeyRef.current === layoutKey) return;
      readyForKeyRef.current = layoutKey;
      onReadyRef.current?.(svg);
    },
    [layoutKey],
  );

  return { fontsReady, setSvgRef, svgRef, signalReady };
}
